import type { Athlete, StatKey } from '../types';

/** Stat block factory keeps roster definitions terse and consistent. */
function stats(s: Partial<Record<StatKey, number>>): Record<StatKey, number> {
  return {
    speed: s.speed ?? 50,
    power: s.power ?? 50,
    agility: s.agility ?? 50,
    stamina: s.stamina ?? 50,
    technique: s.technique ?? 50,
  };
}

/**
 * Starter roster. The comedy is in the contrast (sec. "mismatch comico"): the cheetah
 * is the credible champion, the sloth is the absurd underdog you root for.
 * Data-driven — adding an athlete is one entry, no code.
 */
export const STARTER_ROSTER: Athlete[] = [
  {
    id: 'cheetah_01', species: 'cheetah', name: 'Gary Pardo', nickname: 'Gary', nationality: 'BRA',
    rarity: 'rare', level: 1,
    stats: stats({ speed: 86, power: 60, agility: 78, stamina: 55, technique: 64 }),
    specialty: 'sprint_100m', fatigue: 0, skin: 'cheetah',
  },
  {
    id: 'sloth_01', species: 'sloth', name: 'Brad Ipus', nickname: 'Brad', nationality: 'CRC',
    rarity: 'common', level: 1,
    stats: stats({ speed: 22, power: 70, agility: 30, stamina: 92, technique: 48 }),
    specialty: 'sprint_100m', fatigue: 0, skin: 'sloth',
  },
  {
    id: 'gorilla_01', species: 'gorilla', name: 'Guy Rilla', nickname: 'King', nationality: 'COD',
    rarity: 'rare', level: 1,
    stats: stats({ speed: 48, power: 95, agility: 40, stamina: 70, technique: 52 }),
    specialty: 'sprint_100m', fatigue: 0, skin: 'gorilla',
  },
  {
    id: 'meerkat_01', species: 'meerkat', name: 'Jury Kato', nickname: 'Jury', nationality: 'JPN',
    rarity: 'common', level: 1,
    stats: stats({ speed: 64, power: 38, agility: 88, stamina: 58, technique: 70 }),
    specialty: 'sprint_100m', fatigue: 0, skin: 'meerkat',
  },
  {
    id: 'capybara_01', species: 'capybara', name: 'Cappy Barros', nickname: 'Barry', nationality: 'BRA',
    rarity: 'epic', level: 1,
    stats: stats({ speed: 58, power: 80, agility: 50, stamina: 85, technique: 75 }),
    specialty: 'long_jump', fatigue: 0, skin: 'capybara', unlockPrice: 900,
  },
  {
    id: 'ostrich_01', species: 'ostrich', name: 'Ozzy Strauss', nickname: 'Ozzy', nationality: 'GER',
    rarity: 'rare', level: 1,
    stats: stats({ speed: 92, power: 55, agility: 80, stamina: 62, technique: 50 }),
    specialty: 'sprint_100m', fatigue: 0, skin: 'ostrich', unlockPrice: 500,
  },
  {
    id: 'kangaroo_01', species: 'kangaroo', name: 'Kane Garoo', nickname: 'Joey', nationality: 'AUS',
    rarity: 'epic', level: 1,
    stats: stats({ speed: 70, power: 90, agility: 84, stamina: 66, technique: 68 }),
    specialty: 'long_jump', fatigue: 0, skin: 'kangaroo', unlockPrice: 1200,
  },
  {
    id: 'hare_01', species: 'hare', name: 'Jack Hopwell', nickname: 'Jack', nationality: 'GBR',
    rarity: 'rare', level: 1,
    stats: stats({ speed: 88, power: 50, agility: 94, stamina: 60, technique: 66 }),
    specialty: 'hurdles', fatigue: 0, skin: 'hare', unlockPrice: 450,
  },
  {
    id: 'elephant_01', species: 'elephant', name: 'Eli Fante', nickname: 'Tank', nationality: 'IND',
    rarity: 'epic', level: 1,
    stats: stats({ speed: 38, power: 97, agility: 32, stamina: 82, technique: 58 }),
    specialty: 'javelin', fatigue: 0, skin: 'elephant', unlockPrice: 1200,
  },
  {
    id: 'frog_01', species: 'frog', name: 'Freddy Croak', nickname: 'Freddy', nationality: 'FRA',
    rarity: 'rare', level: 1,
    stats: stats({ speed: 58, power: 76, agility: 92, stamina: 54, technique: 64 }),
    specialty: 'high_jump', fatigue: 0, skin: 'frog', unlockPrice: 600,
  },
  {
    id: 'penguin_01', species: 'penguin', name: 'Pino Guino', nickname: 'Percy', nationality: 'ARG',
    rarity: 'common', level: 1,
    stats: stats({ speed: 40, power: 58, agility: 56, stamina: 72, technique: 50 }),
    specialty: 'high_jump', fatigue: 0, skin: 'penguin', unlockPrice: 300,
  },
  {
    id: 'bear_01', species: 'bear', name: 'Bjorn Ursson', nickname: 'Bjorn', nationality: 'SWE',
    rarity: 'rare', level: 1,
    stats: stats({ speed: 44, power: 93, agility: 36, stamina: 82, technique: 54 }),
    specialty: 'javelin', fatigue: 0, skin: 'bear', unlockPrice: 800,
  },
  {
    id: 'lemur_01', species: 'lemur', name: 'Remy Lure', nickname: 'Remy', nationality: 'MAD',
    rarity: 'epic', level: 1,
    stats: stats({ speed: 78, power: 44, agility: 96, stamina: 60, technique: 82 }),
    specialty: 'hurdles', fatigue: 0, skin: 'lemur', unlockPrice: 1300,
  },
  {
    id: 'octopus_01', species: 'octopus', name: 'Otto Poulos', nickname: 'Otto', nationality: 'GRE',
    rarity: 'epic', level: 1,
    stats: stats({ speed: 66, power: 72, agility: 76, stamina: 64, technique: 60 }),
    specialty: 'sprint_100m', fatigue: 0, skin: 'octopus', unlockPrice: 1500,
  },
  {
    // Italy's Azzurri star — the Apennine wolf. Free, so Italian players have their flag at home.
    id: 'wolf_01', species: 'wolf', name: 'Romolo Lupi', nickname: 'Romolo', nationality: 'ITA',
    rarity: 'epic', level: 1,
    stats: stats({ speed: 86, power: 64, agility: 82, stamina: 80, technique: 60 }),
    specialty: 'sprint_100m', fatigue: 0, skin: 'wolf',
  },
];

export function findAthlete(roster: Athlete[], id: string): Athlete | undefined {
  return roster.find((a) => a.id === id);
}
